<template>
  <div class="filter-bar-wrapper" :class="classModifiers">
    <!-- Bound as value + listener rather than v-model, which drops the update's second argument. -->
    <component
      :is="filter"
      ref="component"
      v-bind="attrs"
      class="--variant-filter-bar"
      hide-pointer
      location="bottom-left"
      :model-value="model"
      :non-clearable="isNonClearable"
      :open="open"
      @update:model-value="handleInnerModelUpdate"
      @update:open="open = $event"
    >
      <template #activator="activatorProps">
        <FilterBarWrapperActivator v-bind="activatorProps" :active="isActive" />
      </template>
    </component>

    <sp-button
      v-if="showCloseAction"
      class="close-action"
      icon="close-bold"
      color="white"
      size="xx-small"
      densitysmcompact="false"
      :label="clearLabelText"
      :data-testid="TestId.finder.filterClear"
      @click="model = undefined"
    ></sp-button>
  </div>
</template>

<script setup>
import { computed, ref, useAttrs, useTemplateRef, watch } from "vue";
import { booleanAttribute, toBoolean, trimToUndefined } from "#ce/utils/props";
import { TestId } from "#/test-ids";
import FilterBarWrapperActivator from "./FilterBarWrapperActivator.ce.vue";

const attrs = useAttrs();

const emit = defineEmits(["update:open", "update:active", "update:model-value"]);

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Object, Array],
    default: undefined,
  },
  filter: {
    type: Object,
    required: true,
  },
  open: {
    type: Boolean,
    default: false,
  },
  nonClearable: booleanAttribute(),
  /**
   * Accessible name for the icon-only clear button.
   * @type {String}
   */
  clearLabel: {
    type: String,
    default: "Clear filter",
  },
});

const isNonClearable = computed(() => toBoolean(props.nonClearable));
const component = useTemplateRef("component");

// A blank clear-label attribute would drop the icon-only button's aria-label.
const clearLabelText = computed(() => trimToUndefined(props.clearLabel) ?? "Clear filter");

const model = ref(trimToUndefined(props.modelValue));
watch(
  () => props.modelValue,
  (value) => (model.value = trimToUndefined(value)),
);
// The options the inner filter sent with the model write the watcher below is about to report.
// Cleared on every report, so the clear button's write carries none.
let innerUpdateOptions;

function handleInnerModelUpdate(value, options) {
  innerUpdateOptions = options;
  model.value = value;
}

// A modelValue prop sync mirrors silently; only the inner filter's own updates report upward.
function emitModelValue(value) {
  const options = innerUpdateOptions;
  innerUpdateOptions = undefined;

  if (value === trimToUndefined(props.modelValue)) {
    return;
  }
  emit("update:model-value", value, options);
}

watch(model, emitModelValue);

const isActive = computed(() => {
  // Complex filters may submit background values that should not make their chip
  // look selected. Honour their explicit opinion; keep the model fallback for all
  // other filters.
  const filterActive = component.value?.active;
  if (filterActive !== undefined) {
    return Boolean(filterActive);
  }

  if (model.value === undefined || model.value === null) {
    return false;
  }

  if (model.value instanceof FormData) {
    return Array.from(model.value.values()).length > 0;
  }

  return true;
});
watch(isActive, (value) => {
  emit("update:active", value);
});

const showCloseAction = computed(() => isActive.value && !isNonClearable.value);

const open = ref(props.open);
watch(
  () => props.open,
  (value) => (open.value = value),
);
watch(open, (value) => {
  emit("update:open", value);
});

const classModifiers = computed(() => ({
  "--is-active": isActive.value,
}));

// Forward the inner filter's surface so the consumer (SpFilterApp) sees the same
// shape as a bare filter, regardless of variant.
defineExpose({
  props: computed(() => component.value?.props),
  filterNames: computed(() => component.value?.filterNames),
  selectedText: computed(() => component.value?.selectedText),
  setFilterValues: (...args) => component.value?.setFilterValues?.(...args),
  setScope: (...args) => component.value?.setScope?.(...args),
});
</script>

<style scoped>
.filter-bar-wrapper {
  position: relative;
}

.close-action {
  display: var(--filter-bar-close-action-display, block);
  position: absolute;
  top: var(--sp-ref-spacing-2);
  right: calc(var(--sp-ref-spacing-6) / 2);
  opacity: 0.3;
  z-index: 2;
  --sp-comp-button-white-text-color: var(--sp-sys-color-primary) !important;
}
.--variant-filter-bar {
  --sp-comp-popup-margin-top: 0.5rem;
}
</style>
